<template>
  <div class="g-page">
    <Spinner v-if="fetching" />
    <div class="footer-margin" v-else>
      <RoomDetails ref="roomDetailsModal" />
      <RateExpiringModal
        @refreshAvailability="resetAvailability"
        ref="rateExpiringModal"
      />
      <section>
        <div class="container-fluid plr-80">
          <div class="property-name-wrapper row">
            <div class="col-md-10 hotel-rating px-md-3 px-0">
              <p>{{ property.name }}</p>
              <h4 class="d-flex align-items-center">
                <span> {{ property.name }}</span>
                <StarRating :value="ratingValue" />
              </h4>
              <div class="access d-flex">
                <li v-if="propertyInfo && propertyInfo.addresses">
                  <a
                    :href="`https://www.google.com/maps?q=${propertyInfo.geoCode?.latitude},${propertyInfo.geoCode?.longitude}`"
                    target="_blank"
                  >
                    {{ parseFullAddress(propertyInfo.addresses[0]) }}</a
                  >
                </li>
                <li v-if="propertyInfo && propertyInfo.geoCode">
                  <a
                    :href="`https://www.google.com/maps?q=${propertyInfo.geoCode?.latitude},${propertyInfo.geoCode?.longitude}`"
                    target="_blank"
                    >{{ $t("listing.showOnMap") }}</a
                  >
                </li>
                <li>
                  <a
                    v-if="propertyData.googleReviews?.googleBusinessPlacesId"
                    target="_blank"
                    :href="`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${propertyData.googleReviews?.googleBusinessPlacesId}`"
                    >{{ $t("property.showInGoogle") }}
                    <img width="12px" src="../assets/icons/g.svg" alt=""
                  /></a>
                </li>
                <li v-if="propertyEmail">
                  <b-icon icon="envelope-fill" class="text-main mr-1"></b-icon>
                  <a :href="`mailto:${propertyEmail}`">{{ propertyEmail }}</a>
                </li>
                <li v-if="propertyPhone">
                  <b-icon icon="telephone-fill" class="text-main mr-1"></b-icon>
                  <a :href="`tel:${propertyPhone}`">{{ propertyPhone }}</a>
                </li>
              </div>
              <DistributorLabel
                v-if="property.distributorType"
                class="mt-3"
                :type="property.distributorType"
                :supplier-id="property.supplier?.id"
              />
            </div>
          </div>
        </div>
      </section>

      <section>
        <MediaList :propertyId="property.id" />
        <BookingCheckout
          :type="property.distributorType"
          :cart="cart"
          :extras="[]"
          v-if="cart.length"
          @proceed="proceedToReservation"
          @clear="cart = []"
        />
      </section>

      <section></section>

      <section>
        <div class="container-fluid plr-80">
          <div class="property-desc">
            <div class="row align-items-center">
              <div class="col-md-12 mb-2 px-md-3 px-0">
                <div v-html="description"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="property.distributorType === 'A'"
        class="book-stay-wrapper"
      >
        <div class="container px-md-0 px-3">
          <BookingEngineDateSelector
            v-if="settingsLoaded"
            :showImmediately="$route.query.showCalendar === 'true'"
            :formatted="dateFormatted"
            :checkIn="date.checkIn"
            :checkOut="date.checkOut"
            :disabled="fetchingAvailability"
            @search="searchCultswitchAvailability"
            :currencyCode="property?.homeCurrency?.code"
            :currencySymbol="property?.homeCurrency?.symbol"
            :distributorType="property?.distributorType"
          />
          <DateSelectorSkeleton v-else />
          <AvailabilitySkeleton v-if="fetchingAvailability" />
          <CultswitchList
            v-else
            :availabilities="availabilities"
            :fetched="availabilitiesFetched"
            :check-in="date.checkIn"
            :check-out="date.checkOut"
            :hotel-email="propertyEmail"
            @cartUpdated="updateCart"
            :promoCode="promoCode"
            :stayMode="stayMode"
            @openDetails="showRoomModal"
          />
        </div>
      </section>

      <template v-else-if="property.distributorType === 'B' && property.provider">
        <section class="book-stay-wrapper">
          <div class="container px-md-0 px-3">
            <BookingEngineDateSelector
              :formatted="dateFormatted"
              :checkIn="date.checkIn"
              :checkOut="date.checkOut"
              :disabled="fetchingAvailability"
              @search="searchTypeBAvailability"
              :currencyCode="property?.homeCurrency?.code"
              :currencySymbol="property?.homeCurrency?.symbol"
              :distributorType="property?.distributorType"
              :provider="property.provider"
              :guestData="guestData"
            />
            <AvailabilitySkeleton v-if="fetchingAvailability || typeBFetching" />
            <template v-else>
              <NuiteeList
                v-if="typeBProvider === 'NUITEE'"
                :property="property"
                :availabilities="availabilities"
                :fetched="availabilitiesFetched"
                :check-in="date.checkIn"
                :check-out="date.checkOut"
                @bookNow="bookNuiteeProduct"
                @openDetails="showRoomModal"
              />
              <EhotelList
                v-else
                :property="property"
                :availabilities="availabilities"
                :fetched="availabilitiesFetched"
                :check-in="date.checkIn"
                :check-out="date.checkOut"
                @bookNow="bookEhotelProduct"
                @openDetails="showRoomModal"
              />
            </template>
          </div>
        </section>
      </template>

      <BookingRequestForm
        v-else-if="['C', 'D'].includes(property.distributorType)"
        :property="property"
        :propertyInfo="propertyInfo"
      />

      <div class="container-fluid plr-80 my-5 py-5" v-else>
        <b-alert class="py-4" show variant="warning">{{
          $t("property.noDistributor")
        }}</b-alert>
      </div>

      <div class="container-fluid plr-80 my-5" v-if="hasFacilities">
        <div class="font-bold mb-2">
          <h4 class="mb-3" style="font-weight: 600">
            {{ $t("property.facilitiesAndExtras") }}
          </h4>
        </div>
        <hr />
        <div v-if="isGroupedFacilities" class="row">
          <div
            class="col-md-3 pb-2"
            v-for="(list, index) in groupedFacilities"
            :key="index"
          >
            <div v-for="({ name, items }, idx) in list" :key="`${name}-${idx}`" class="mb-3">
              <div class="d-flex">
                <b-icon icon="diamond-fill" class="text-main"></b-icon>
                <h6 class="ml-2" style="font-weight: 600">{{ name }}</h6>
              </div>
              <div
                v-for="(facility, idxx) in items"
                :key="`${name}-${idx}-${idxx}-${formatFacilityLabel(facility)}`"
              >
                <template v-if="formatFacilityLabel(facility)">
                  <b-icon icon="arrow-right-short" class="text-main"></b-icon>
                  <span class="ml-2">{{ langNameFromCode(formatFacilityLabel(facility)) }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="row">
          <div
            class="col-md-3 pb-2"
            v-for="(column, index) in ungroupedFacilityColumns"
            :key="index"
          >
            <div
              v-for="(facility, idx) in column"
              :key="`${facility}-${idx}`"
              class="mb-2 d-flex align-items-start"
            >
              <b-icon icon="arrow-right-short" class="text-main mt-1"></b-icon>
              <span class="ml-2">{{ langNameFromCode(facility) }}</span>
            </div>
          </div>
        </div>
      </div>

      <Footer ref="footerRef"></Footer>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions } from "vuex";
import Spinner from "./Widgets/Spinner.vue";
import { formatAddressText } from "../helpers";
import MediaList from "./Property/MediaList.vue";
import { getClientCountryCode } from "../helpers";
import { paymentExpiryMs } from "../shared";
import StarRating from "./Widgets/StarRating.vue";
import EhotelList from "./Property/EhotelList.vue";
import NuiteeList from "./Property/NuiteeList.vue";
import RoomDetails from "./Property/RoomDetails.vue";
import TranslationHelper from "../helpers/translation";
import CultswitchList from "./Property/CultswitchList.vue";
import BookingCheckout from "./Widgets/BookingCheckout.vue";
import DistributorLabel from "./Property/DistributorLabel.vue";
import RateExpiringModal from "./Widgets/RateExpiringModal.vue";
import BookingRequestForm from "./Property/BookingRequestForm.vue";
import AvailabilitySkeleton from "./Skeletons/AvailabilitySkeleton.vue";
import DateSelectorSkeleton from "./Skeletons/DateSelectorSkeleton.vue";
import BookingEngineDateSelector from "./Widgets/BookingEngineDateSelector.vue";
import { applyPropertyPageSeo } from "../helpers/theme";
import { trackCheckoutSelection } from "../helpers/analytics";

export default {
  name: "Property",
  components: {
    Spinner,
    MediaList,
    EhotelList,
    NuiteeList,
    StarRating,
    RoomDetails,
    CultswitchList,
    BookingCheckout,
    DistributorLabel,
    RateExpiringModal,
    BookingRequestForm,
    DateSelectorSkeleton,
    AvailabilitySkeleton,
    BookingEngineDateSelector,
  },

  data() {
    return {
      promoCode: "",
      date: {
        checkIn: null,
        checkOut: null,
      },

      cart: [],
      facilities: { grouped: true, data: [] },
      availabilities: [],

      guestData: null,
      typeBProvider: null,
      clientCountryCode: null,

      stayMode: false,
      dateFormatted: false,
      typeBFetching: false,
      settingsLoaded: false,
      availabilitiesFetched: false,
    };
  },
  computed: {
    ...mapState("property", [
      "propertyData",
      "fetching",
      "fetchingAvailability",
    ]),

    property() {
      return this.propertyData?.property;
    },
    propertyInfo() {
      return this.propertyData?.propertyInfo;
    },
    propertyEmail() {
      const email = this.property.emails.find(
        ({ emailType }) => emailType.code?.toLowerCase() === "primary"
      )?.email;
      return email || this.property.emails[0]?.email || "";
    },
    propertyPhone() {
      return this.property.phones.find(
        ({ phoneType }) => phoneType.name?.toLowerCase() === "primary_e164"
      )?.phoneNumber || this.property.phones[0]?.phoneNumber || "";
    },
    ratingValue() {
      const value = this.propertyData?.googleReviews?.googleRating || 0;
      return parseFloat(value.toFixed(1));
    },
    description() {
      const descriptionsRaw = this.property?.descriptions;
      if (!descriptionsRaw?.length) return "";

      const locale = localStorage.getItem("locale") || "en";

      const descriptions = descriptionsRaw.filter(
        (desc) => desc.descriptionType?.code === "hotel-long"
      );

      let description = descriptions.find(
        (desc) => desc.language?.code === locale
      );

      if (!description) {
        description = descriptions.find((desc) => desc.language?.code === "en");
      }

      return (description || descriptions[0])?.text || "";
    },
    facilityItems() {
      return this.facilities?.data || [];
    },
    isGroupedFacilities() {
      if (this.facilities?.grouped === false) {
        return false;
      }

      if (this.facilities?.grouped === true) {
        return true;
      }

      return this.facilityItems.every((item) => Array.isArray(item?.items));
    },
    hasFacilities() {
      return this.facilityItems.length > 0;
    },
    groupedFacilities() {
      const columns = [[], [], [], []];
      const columnCounts = [0, 0, 0, 0];

      if (!this.isGroupedFacilities || !this.facilityItems.length) {
        return columns;
      }

      for (const group of this.facilityItems) {
        const minIndex = columnCounts.indexOf(Math.min(...columnCounts));

        columns[minIndex].push(group);
        columnCounts[minIndex] += (group.items?.length || 0) + 1;
      }

      return columns;
    },
    ungroupedFacilities() {
      if (this.isGroupedFacilities) {
        return [];
      }

      return this.facilityItems
        .map((item) => this.formatFacilityLabel(item))
        .filter(Boolean);
    },
    ungroupedFacilityColumns() {
      const columns = [[], [], [], []];
      const columnCounts = [0, 0, 0, 0];

      for (const facility of this.ungroupedFacilities) {
        const minIndex = columnCounts.indexOf(Math.min(...columnCounts));

        columns[minIndex].push(facility);
        columnCounts[minIndex] += 1;
      }

      return columns;
    },
    ehotelPayload() {
      const { checkIn, checkOut } = this.date;
      const { ehotelGuestData } = this.guestData || {};
      return {
        check_in: checkIn,
        check_out: checkOut,
        property_id: this.property.id,
        ehotel_guest_data: ehotelGuestData || { rooms: 1, persons: 2 },
      };
    },
    nuiteePayload() {
      const { checkIn, checkOut } = this.date;
      const { nuiteeGuestsData } = this.guestData || {};
      return {
        check_in: checkIn,
        check_out: checkOut,
        property_id: this.property.id,
        nationality: this.clientCountryCode || "us",
        currency: this.homeCurrency?.code || "USD",
        nuitee_guest_data: nuiteeGuestsData || {
          rooms: 1,
          adults: 2,
          children: 0,
          childrenAges: [],
        },
      };
    },
    getGuestData() {
      let { ehotelGuestData, nuiteeGuestsData } = this.guestData ?? {};
      if (!ehotelGuestData) {
        ehotelGuestData = {
          rooms: 1,
          persons: 2,
        }
      }
      if (!nuiteeGuestsData) {
        nuiteeGuestsData = {
          rooms: 1,
          adults: 2,
          children: 0,
          childrenAges: [],
        };
      }
      return {
        ehotelGuestData,
        nuiteeGuestsData,
      };
    },
  },
  watch: {
    fetching(isFetching) {
      if (!isFetching && this.property?.name) {
        this.$nextTick(() => applyPropertyPageSeo(this.property));
      }
    },
    description() {
      if (!this.fetching && this.property?.name) {
        applyPropertyPageSeo(this.property);
      }
    },
  },
  methods: {
    ...mapActions("property", [
      "getPropertyById",
      "fetchPropertyMedia",
      "getPropertySettings",
      "fetchPropertyFacilities",
      "findEhotelAvailability",
      "findNuiteeAvailability",
      "findCultswitchAvailability",
      "fetchAvailabilityDefaultMedia",
    ]),
    showRoomModal(media) {
      this.$refs.roomDetailsModal.setMedia(media);
      this.$refs.roomDetailsModal.show();
    },
    normalizeAvailabilityList(response) {
      const list = response?.data;
      return Array.isArray(list) ? list : [];
    },
    fetchTypeBAvailability(provider, request) {
      return request.then((response) => {
        const data = this.normalizeAvailabilityList(response?.data);
        if (data?.length === 0) {
          throw new Error(`${provider} returned no availability`);
        }
        return { provider, data }
      });
    },
    async getCachedEhotelAvailability() {
      const { data } = await this.findEhotelAvailability({
        ...this.ehotelPayload,
        cache: true,
      });
      return this.normalizeAvailabilityList(data);
    },
    async searchTypeBAvailability({ checkIn, checkOut, nuiteeGuestsData, ehotelGuestData }, setDate = true) {
      this.typeBFetching = true;
      if (setDate) {
        this.date.checkIn = checkIn;
        this.date.checkOut = checkOut;
      }
      this.guestData = {
        ehotelGuestData,
        nuiteeGuestsData,
      };

      this.typeBProvider = this.property?.provider || "EHOTEL";
      this.availabilities = [];
      this.availabilitiesFetched = false;

      try {
        const cached = await this.getCachedEhotelAvailability();
        if (cached.length > 0) {
          this.typeBProvider = "EHOTEL";
          this.availabilities = cached;
          this.availabilitiesFetched = true;
          this.typeBFetching = false;
        }
      } catch (error) {
        console.error(this.$t("property.availabilityError"));
      }

      const showLoading = this.availabilities.length === 0;

      try {
        const winner = await Promise.any([
          this.fetchTypeBAvailability(
            "EHOTEL",
            this.findEhotelAvailability({
              ...this.ehotelPayload,
              _load: showLoading,
            })
          ),
          this.fetchTypeBAvailability(
            "NUITEE",
            this.findNuiteeAvailability({
              ...this.nuiteePayload,
              _load: showLoading,
            })
          ),
        ]);

        this.typeBProvider = winner.provider;
        this.availabilities = winner.data;
        this.startExpiryCountdown();
      } catch (error) {
        this.availabilities = [];
      } finally {
        this.availabilitiesFetched = true;
      }
      this.typeBFetching = false;
    },
    async searchCultswitchAvailability(
      { checkIn, checkOut, promoCode },
      setDate = true
    ) {
      if (setDate) {
        this.date.checkIn = checkIn;
        this.date.checkOut = checkOut;
      }
      this.stayMode = moment(checkOut).diff(moment(checkIn), "days", true) > 1;
      this.promoCode = promoCode;
      try {
        const { data } = await this.findCultswitchAvailability({
          check_in: checkIn,
          check_out: checkOut,
          property_id: this.property.id,
          promo_code: promoCode,
        });
        this.availabilitiesFetched = true;
        this.availabilities = data?.data?.rooms || [];
        this.startExpiryCountdown();
      } catch (error) {
        this.$toastr.e(error.message);
      }
    },
    startExpiryCountdown() {
      if (this.availabilities.length > 0 && this.$refs.rateExpiringModal) {
        this.$refs.rateExpiringModal.startRateExpiryTimer();
      }
    },
    async resetAvailability() {
      const { distributorType } = this.property;
      let { checkIn, checkOut } = this.date;
      if (distributorType === "A") {
        const promoCode = this.promoCode;
        await this.searchCultswitchAvailability({
          checkIn,
          checkOut,
          promoCode,
        });
      } else if (distributorType === "B") {
        await this.searchTypeBAvailability({
          checkIn,
          checkOut,
          ...this.guestData,
        });
      }
    },
    updateCart(cartItems) {
      this.cart = cartItems;
    },
    bookEhotelProduct(cartItem) {
      this.cart = [cartItem];
      this.proceedToReservation("EHOTEL");
    },
    bookNuiteeProduct(cartItem) {
      this.cart = [cartItem];
      this.proceedToReservation("NUITEE");
    },
    proceedToReservation(provider = "") {
      const paymentExpiryKey = `checkoutPaymentExpiry-${this.property.id}`;
      const paymentExpiry = Date.now() + paymentExpiryMs;
      const checkoutData = {
        provider,
        date: this.date,
        cart: this.cart,
        extras: [],
        property: this.property,
        promoCode: this.promoCode,
        distributorType: this.property.distributorType,
        propertyId: this.property.id,
      };
      sessionStorage.setItem(
        `checkoutData-${this.property.id}`,
        JSON.stringify(checkoutData)
      );
      sessionStorage.setItem(paymentExpiryKey, paymentExpiry.toString());
      trackCheckoutSelection(checkoutData);
      this.$router.push(`/listing/${this.property.id}/reservation`);
    },
    formatFacilityLabel(facility) {
      if (typeof facility === "string") {
        return facility;
      }

      return facility?.name || "";
    },
    langNameFromCode(text) {
      const locales = TranslationHelper.getLocales();
      if (text) {
        const locale = locales.find(({ code }) => code === text.trim());
        if (locale) {
          return locale.title;
        }
      }
      return text;
    },
    parseFullAddress(address) {
      if (!address) {
        return "";
      }

      const { addressLine, cityName, postalCode, country } = address;
      return formatAddressText([addressLine, cityName, postalCode, country?.code].join(", "))
    },
  },
  async mounted() {
    const { id } = this.$route.params;
    await this.fetchAvailabilityDefaultMedia(id);
  },
  async created() {
    const { id } = this.$route.params;
    let { checkIn, checkOut, nuiteeGuest, eHotelGuest, findAvailability } = this.$route.query;
    if (checkIn && checkOut) {
      checkIn = moment(checkIn, "YYYY-MM-DD").format("DD-MM-YYYY");
      checkOut = moment(checkOut, "YYYY-MM-DD").format("DD-MM-YYYY");
    } else {
      checkIn = moment(new Date()).add(1, "day").format("DD-MM-YYYY");
      checkOut = moment(new Date()).add(2, "days").format("DD-MM-YYYY");
    }

    if (!id) {
      this.$router.push("/listing");
    }

    try {
      await this.getPropertyById(id);
    } catch (error) {
      this.$toastr.e(error.message);
    }

    const { distributorType, provider } = this.property;
    this.date.checkIn = checkIn;
    this.date.checkOut = checkOut;

    const nuiteeGuestsData = nuiteeGuest ? JSON.parse(nuiteeGuest) : null;
    const ehotelGuestData = eHotelGuest ? JSON.parse(eHotelGuest) : null;
    if (nuiteeGuestsData && ehotelGuestData) {
      this.guestData = {
        nuiteeGuestsData,
        ehotelGuestData,
      };
    }

    if (findAvailability === "true") {
      checkIn = moment(checkIn, "DD-MM-YYYY").format("YYYY-MM-DD");
      checkOut = moment(checkOut, "DD-MM-YYYY").format("YYYY-MM-DD");
      const payload = { checkIn, checkOut };
      this.date = payload;
      this.dateFormatted = true;
      if (distributorType === "A") {
        await this.searchCultswitchAvailability(payload, false);
      } else if (distributorType === "B") {
        await this.searchTypeBAvailability({ ...payload, ...this.getGuestData }, false);
      }
    }
    const facilitiesResponse = await this.fetchPropertyFacilities(id);
    if (facilitiesResponse?.grouped !== undefined) {
      this.facilities = facilitiesResponse;
    } else if (facilitiesResponse?.data?.grouped !== undefined) {
      this.facilities = facilitiesResponse.data;
    } else {
      this.facilities = {
        grouped: true,
        data: Array.isArray(facilitiesResponse?.data) ? facilitiesResponse.data : [],
      };
    }
    await this.getPropertySettings(id);
    this.settingsLoaded = true;

    if (distributorType === "B" && provider === "NUITEE") {
      this.clientCountryCode = await getClientCountryCode();
    }
  },
};
</script>
